import {getApiBaseUrl, getApiUrl, notifications} from "../global";
import axios from "axios";
import {getStoredSession} from "../LocalStorageService";
import {_buildQueryString, CACHE_INVALIDATION_MAP, cacheGet, cacheSet, cacheInvalidatePrefix} from "./cacheUtils";

// ── Internal helpers ─────────────────────────────────────────────────────────

function _buildApiUrl(path) {
    return getApiUrl() + getApiBaseUrl() + path;
}

/**
 * Generic HTTP request. Returns response.data on success, undefined on error.
 * @param {'get'|'post'|'patch'|'put'|'delete'} method
 * @param {string} path - API path (appended to base URL)
 * @param {object|null} [data] - Request payload (POST/PATCH/PUT)
 * @param {object} [options]
 * @param {number[]} [options.accepted] - Valid status codes (default [200])
 * @param {object} [options.config] - Axios config (signal, onUploadProgress, responseType, etc.)
 * @returns {Promise<*>} response.data or undefined on error
 */
const _inflight = new Map(); // path → in-flight Promise (deduplicates simultaneous identical GET calls)

function _request(method, path, data = null, { accepted = [200], config = {}, cache = true } = {}) {
    // GET — check cache then in-flight dedup before fetching
    if (method === 'get' && cache) {
        const cached = cacheGet(path);
        if (cached !== null) return Promise.resolve(cached);
        if (_inflight.has(path)) return _inflight.get(path);
    }

    const url = _buildApiUrl(path);
    const args = data !== null ? [url, data, config] : [url, config];
    const promise = axios[method](...args)
        .then(response => {
            if (accepted.includes(Number(response?.status))) {
                const result = response?.data;

                if (method === 'get' && cache && result !== undefined) {
                    cacheSet(path, result);
                } else if ((method === 'patch' || method === 'put') && result !== undefined) {
                    cacheSet(path, result);
                    // also invalidate the parent list — list contains denormalised fields of this resource
                    const patchEntry = CACHE_INVALIDATION_MAP.find(e => path.match(e.pattern));
                    if (patchEntry) cacheInvalidatePrefix(patchEntry.prefix(path.match(patchEntry.pattern)));
                } else if (method === 'post') {
                    cacheInvalidatePrefix(path);
                } else if (method === 'delete') {
                    const entry = CACHE_INVALIDATION_MAP.find(e => path.match(e.pattern));
                    if (entry) cacheInvalidatePrefix(entry.prefix(path.match(entry.pattern)));
                }

                return result;
            }
            throw new Error(`Unexpected status code: ${response?.status}`);
        })
        .catch(error => {
            console.error(error);
            throw error; // Re-throw error to allow the caller to handle it.
        })
        .finally(() => { if (method === 'get' && cache) _inflight.delete(path); });

    if (method === 'get' && cache) _inflight.set(path, promise);
    return promise;
}

// ── Validation helper ────────────────────────────────────────────────────────

function _validateParams(params) {
    Object.entries(params).forEach(([name, value]) => {
        if (!value) {
            const label = name
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, s => s.toUpperCase())
                .replace(/ Id/g, ' ID')
                .trim();
            throw new Error(`${label} is required`);
        }
    });
}

// ------------------------------------------------
// Customer contacts cache (platform users + shipment contacts)
// ------------------------------------------------

const _contactsCache = {};
const CONTACTS_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Builds a stable cache key for a user/contact entry.
 * Platform users: keyed by uuid.
 * Shipment contacts: keyed by email (primary) or first_name|last_name|phone_number (fallback).
 */
export function buildContactKey(user) {
    if (user.uuid) return user.uuid;
    if (user.email || user.optional_email) return user.email || user.optional_email;
    const parts = [user.first_name, user.last_name, user.phone_number].filter(Boolean);
    return parts.length ? parts.join('|') : null;
}

/**
 * Invalidates a single user/contact entry in the cache for a customer.
 * Call when a specific user's data is confirmed to have mutated.
 */
export function invalidateUserContactEntry(customerId, userKey) {
    if (_contactsCache[customerId] && userKey) {
        delete _contactsCache[customerId][userKey];
    }
}

/**
 * Invalidates the entire contact cache for a customer.
 * Call when a new user/contact is added (no existing key to target).
 */
export function invalidateCustomerContactCache(customerId) {
    delete _contactsCache[customerId];
}

function _isContactCacheValid(customerId) {
    const bucket = _contactsCache[customerId];
    return bucket && (Date.now() - bucket._timestamp) < CONTACTS_TTL;
}

function _getCachedContacts(customerId) {
    const bucket = _contactsCache[customerId];
    if (!bucket) return null;
    const { _timestamp, ...entries } = bucket;
    return entries;
}

function _setCacheEntry(customerId, key, value) {
    if (!_contactsCache[customerId]) {
        _contactsCache[customerId] = {_timestamp: Date.now()};
    }
    _contactsCache[customerId][key] = value;
}

/**
 * Returns all known contacts for a customer, grouped by category.
 * Platform users (have uuid/role/login) + shipment contacts (flat fields, no uuid).
 *
 * @param {string} customerId - Customer UUID
 * @param {object} [options]
 * @param {boolean} [options.forceRefresh=false] - Bypass TTL cache
 * @returns {Promise<{ platform: Array, contacts: Array }>}
 */
export async function fetchAllCustomerContacts(customerId, { forceRefresh = false } = {}) {
    if (!customerId) return { platform: [], contacts: [] };

    if (!forceRefresh && _isContactCacheValid(customerId)) {
        const cached = _getCachedContacts(customerId);
        const platform = Object.values(cached).filter(u => u._category === 'platform');
        const contacts = Object.values(cached).filter(u => u._category === 'contact');
        return { platform, contacts };
    }

    // Fetch platform users + jobs (for shipment contacts) in parallel
    const [usersRes, ordersRes] = await Promise.all([
        fetchCustomerUsers(customerId).catch(() => null),
        fetchJobsByCustomer(customerId).catch(() => null),
    ]);

    const platformUsers = (usersRes?.results || []);

    // Build set of platform user emails for cross-category dedup
    const platformEmails = new Set(platformUsers.map(u => u.email).filter(Boolean));

    // Extract unique contacts from shipments and tape return contacts —
    // skip if email matches a platform user (platform user wins)
    const contactMap = {};

    const addContact = (email, first_name, last_name, phone_number, mobile_number) => {
        if (email && platformEmails.has(email)) return; // platform user wins
        const contact = {
            title:        undefined,
            first_name,
            middle_name:  undefined,
            last_name,
            phone_number,
            mobile_number,
            email,
            _category:    'contact',
        };
        const key = buildContactKey(contact);
        if (key && !contactMap[key]) contactMap[key] = contact;
    };

    (ordersRes || []).forEach(order => {
        // Shipment contacts
        (order.shipments || []).forEach(shipment => {
            addContact(
                shipment.optional_email,
                shipment.first_name,
                shipment.last_name,
                shipment.phone_number,
                shipment.mobile_number,
            );
        });
        // Tape return contact
        if (order.tape_return_first_name || order.tape_return_last_name || order.tape_return_email) {
            addContact(
                order.tape_return_email,
                order.tape_return_first_name,
                order.tape_return_last_name,
                order.tape_return_phone_number,
                order.tape_return_mobile_number,
            );
        }
    });

    const shipmentContacts = Object.values(contactMap);

    // Reset cache bucket and populate
    _contactsCache[customerId] = {_timestamp: Date.now()};
    platformUsers.forEach(u => {
        const key = buildContactKey(u);
        if (key) _setCacheEntry(customerId, key, {...u, _category: 'platform'});
    });
    shipmentContacts.forEach(c => {
        const key = buildContactKey(c);
        if (key) _setCacheEntry(customerId, key, c);
    });

    return { platform: platformUsers, contacts: shipmentContacts };
}

// ------------------------------------------------
// Jobs
// ------------------------------------------------

export function fetchJobs({jobTypes = [], jobStatuses = [], search, page, customerNames = []} = {}) {
    const qs = _buildQueryString({
        customer__company_name: customerNames,
        order_type: jobTypes,
        page,
        search,
        status: jobStatuses,
    });
    return _request('get', `/jobs/${qs}`, null, {cache: !search});
}

/**
 * @param {string} customerId
 * @param {{background?: boolean}} [options] - `background: true` keeps every page of the walk out of
 *   the global loading wash. Opt-in for the same reason as fetchCustomers: fetchAllCustomerContacts
 *   calls this after a click. It matters more here than there, because this paginates — an account
 *   with several hundred jobs holds the wash up for one request after another.
 */
export async function fetchJobsByCustomer(customerId, {background = false} = {}) {
    const baseUrl = _buildApiUrl('/jobs/');
    const pageSize = 100;
    let page = 1;
    let allJobs = [];

    while (true) {
        // By removing .catch(), we allow errors to be thrown and handled by the calling component.
        const response = await axios.get(baseUrl, {params: { customer__uuid: customerId, page, page_size: pageSize }, background});

        const results = response?.data?.results || [];
        allJobs = allJobs.concat(results);

        if (results.length < pageSize) break;
        page++;
    }

    return allJobs;
}

export function fetchJob(jobId) {
    return _request('get', `/jobs/${jobId}/`);
}

export function deleteJob(jobId) {
    _validateParams({jobId});
    return _request('delete', `/jobs/${jobId}/`, null, {accepted: [200, 204]});
}

export function createJob(data) {
    _validateParams({data});
    if (!data?.customer) throw new Error("Customer ID is required");
    return _request('post', '/jobs/', data, {accepted: [200, 201]});
}

export function patchJob(jobId, customerId, data) {
    return _request('patch', `/jobs/${jobId}/`, {customer: customerId, ...data});
}

// ------------------------------------------------
// Customers
// ------------------------------------------------

/**
 * @param {string} [search]
 * @param {number|null} [page]
 * @param {{background?: boolean, signal?: AbortSignal}} [options]
 *   `background: true` keeps the call out of the global loading wash. Opt-in rather than the default
 *   because most callers fetch this after a click and the reader *is* waiting on it — Job.jsx and
 *   home.jsx's getClientList both want the wash. Only the header's job-list fetch on mount is nobody's
 *   request, and washing the portal out for it swallowed every click on the sidebar for as long as it
 *   took.
 *
 *   `signal` aborts the request, for a caller that unmounts while it is still out. Note that a cached
 *   GET is deduplicated by path, so if two components ask at the same moment they share one promise and
 *   an abort by either rejects it for both. Only jobList.jsx passes a signal today, and it is the only
 *   reader of the customer list on that page, so this is recorded rather than worked around.
 */
export function fetchCustomers(search = "", page = null, {background = false, signal} = {}) {
    return _request('get', `/customers/${_buildQueryString({search, page})}`, null, {cache: !search, config: {background, signal}})
        .then(data => {
            if (Array.isArray(data)) data.sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
            return data;
        });
}

export function fetchCustomer(customerId) {
    return _request('get', `/customers/${customerId}/`);
}

export function patchCustomer(customerId, data) {
    return _request('patch', `/customers/${customerId}/`, {customer: customerId, ...data});
}

export function deleteCustomer(customerId) {
    _validateParams({customerId});
    return _request('delete', `/customers/${customerId}/`, null, {accepted: [200, 204]});
}

export function createCustomer(data) {
    _validateParams({data});
    return _request('post', '/customers/', data, {accepted: [200, 201]});
}

export function fetchCustomerUsers(customerId) {
    _validateParams({customerId});
    return _request('get', `/customers/${customerId}/users/`);
}

export function deleteCustomerUser(customerId, userId) {
    _validateParams({customerId, userId});
    return _request('delete', `/customers/${customerId}/users/${userId}/`, null, {accepted: [200, 204]})
        .then(data => { invalidateCustomerContactCache(customerId); return data; });
}

export function createCustomerUser(customerId, data) {
    _validateParams({customerId, data});
    return _request('post', `/customers/${customerId}/users/`, data, {accepted: [200, 201]})
        .then(result => { invalidateCustomerContactCache(customerId); return result; });
}

// ------------------------------------------------
// Microsoft Entra sign-in and admin consent
// ------------------------------------------------

/**
 * Who this company's Microsoft administrator is, and how far consent has got.
 *
 * A saved admin_email is the setting: it is what says this company signs in with Entra, and clearing it
 * takes them back off. There is no separate flag, because a flag and an address can disagree and then
 * neither means anything.
 *
 * Not cached. The other two calls below both return the same shape, so the screen is kept current by
 * their responses rather than by re-reading, and a cached copy would only make an address somebody has
 * just edited elsewhere look unedited here.
 */
export function fetchEntraSignIn(customerId) {
    _validateParams({customerId});
    return _request('get', `/customers/${customerId}/entra-sign-in/`, null, {cache: false});
}

/** Store the administrator's address without sending anything. An empty one takes the company off Entra. */
export function saveEntraSignIn(customerId, data) {
    _validateParams({customerId, data});
    return _request('patch', `/customers/${customerId}/entra-sign-in/`, data, {cache: false});
}

/**
 * Save and send the consent email in one request.
 *
 * One call rather than save-then-send because a half-completed pair leaves the worst possible state: an
 * address stored, the operator believing the email went, and the customer waiting for something nobody
 * sent. The API sends only after the save has committed.
 */
export function sendEntraConsentInvite(customerId, data) {
    _validateParams({customerId, data});
    return _request('post', `/customers/${customerId}/entra-sign-in/send/`, data, {accepted: [200, 201], cache: false});
}

// ------------------------------------------------
// Shipments
// ------------------------------------------------

export function fetchShipmentDetails(jobId) {
    return _request('get', `/jobs/${jobId}/shipments/`);
}

export function fetchShipmentDetail(jobId, shipmentId) {
    _validateParams({jobId, shipmentId});
    return _request('get', `/jobs/${jobId}/shipments/${shipmentId}/`);
}

export function deleteShipment(jobId, shipmentId, {customerId} = {}) {
    _validateParams({jobId, shipmentId});
    return _request('delete', `/jobs/${jobId}/shipments/${shipmentId}/`, null, {accepted: [200, 204]})
        .then(data => { if (customerId) invalidateCustomerContactCache(customerId); return data; });
}

export function addShipment(data, {customerId} = {}) {
    _validateParams({data});
    if (!data?.shipping_address && !data?.tracking_link) throw new Error("Shipping address or tracking link is required");
    if (!data?.order) throw new Error("Order ID is required");
    return _request('post', `/jobs/${data.order}/shipments/`, data, {accepted: [200, 201]})
        .then(result => { if (customerId) invalidateCustomerContactCache(customerId); return result; });
}

export function patchShipment(jobId, shipmentId, data, {customerId} = {}) {
    _validateParams({jobId, shipmentId, data});
    return _request('patch', `/jobs/${jobId}/shipments/${shipmentId}/`, {uuid: shipmentId, ...data}, {accepted: [200, 204]})
        .then(result => { if (customerId) invalidateCustomerContactCache(customerId); return result; });
}

// ------------------------------------------------
// Tapes
// ------------------------------------------------

export function fetchTapes(jobId) {
    _validateParams({jobId});
    return _request('get', `/jobs/${jobId}/tapes/`);
}

export function addTape(jobId, data) {
    _validateParams({jobId, data});
    return _request('post', `/jobs/${jobId}/tapes/`, data, {accepted: [200, 201]});
}

export function updateTape(jobId, tapeId, data) {
    _validateParams({jobId, tapeId, data});
    return _request('put', `/jobs/${jobId}/tapes/${tapeId}/`, data, {accepted: [200, 204]});
}

export function deleteTape(jobId, tapeId) {
    _validateParams({jobId, tapeId});
    return _request('delete', `/jobs/${jobId}/tapes/${tapeId}/`, null, {accepted: [200, 204]});
}

// ------------------------------------------------
// Job Documents
// ------------------------------------------------

export function fetchJobDocuments(jobId) {
    _validateParams({jobId});
    return _request('get', `/jobs/${jobId}/documents/`);
}

/**
 * Upload a document to a job.
 * @param {string} jobId - The job UUID.
 * @param {object} data - Document payload (name, file_type, job, file).
 * @param {object} [options] - Optional config.
 * @param {function} [options.onProgress] - Callback receiving axios ProgressEvent during upload.
 * @param {AbortSignal} [options.signal] - AbortController signal to cancel the request.
 * @returns {Promise<object|undefined>} Created document data or undefined on error.
 */
export function createJobDocument(jobId, data, { onProgress, signal } = {}) {
    _validateParams({jobId, data});
    const config = {};
    if (onProgress) config.onUploadProgress = onProgress;
    if (signal) config.signal = signal;
    return _request('post', `/jobs/${jobId}/documents/`, data, {accepted: [200, 201], config});
}

export function downloadJobDocument(jobId, documentId) {
    _validateParams({jobId, documentId});
    return _request('get', `/jobs/${jobId}/documents/${documentId}/download/`, null, {config: {responseType: 'blob'}, cache: false});
}

export function deleteJobDocument(jobId, documentId) {
    _validateParams({jobId, documentId});
    return _request('delete', `/jobs/${jobId}/documents/${documentId}/`, null, {accepted: [200, 204]});
}

// ------------------------------------------------
// Fees
// ------------------------------------------------

export function fetchFees(jobId, page = 1) {
    _validateParams({jobId});
    return _request('get', `/jobs/${jobId}/fees/${_buildQueryString({page})}`)
}

export function deleteFee(jobId, feeId) {
    _validateParams({jobId, feeId});
    return _request('delete', `/jobs/${jobId}/fees/${feeId}/`, null, {accepted: [200, 204]});
}

export function createFee(jobId, payload) {
    _validateParams({jobId, payload});
    return _request('post', `/jobs/${jobId}/fees/`, payload, {accepted: [200, 201]});
}

export function saveFee(jobId, feeId, payload) {
    _validateParams({jobId, feeId, payload});
    return _request('put', `/jobs/${jobId}/fees/${feeId}/`, payload);
}

// ------------------------------------------------
// Shipment Verifications
// ------------------------------------------------

export function fetchVerificationsDetails(jobId, shipmentId) {
    _validateParams({jobId, shipmentId});
    return _request('get', `/jobs/${jobId}/shipments/${shipmentId}/verifications/`);
}

export function deleteVerification(jobId, shipmentId, verificationId) {
    _validateParams({jobId, shipmentId, verificationId});
    return _request('delete', `/jobs/${jobId}/shipments/${shipmentId}/verifications/${verificationId}/`, null, {accepted: [200, 204]});
}

export function patchVerification(jobId, shipmentId, verificationId, data) {
    _validateParams({jobId, shipmentId, verificationId, data});
    return _request('patch', `/jobs/${jobId}/shipments/${shipmentId}/verifications/${verificationId}/`, data, {accepted: [200, 201, 204]});
}

export function addVerification(jobId, shipmentId, data) {
    _validateParams({jobId, shipmentId, data});
    return _request('post', `/jobs/${jobId}/shipments/${shipmentId}/verifications/`, data, {accepted: [200, 201]});
}

// ------------------------------------------------
// Audit Logs
// ------------------------------------------------

export function fetchAuditLogs(page = 1) {
    return _request('post', `/auditlogs/?page=${page}`, {search: ""});
}

// ------------------------------------------------
// Notifications
// ------------------------------------------------

/**
 * @param {{background?: boolean}} [options] - `background: true` keeps the poll out of the global
 *   loading wash. The header reads notifications on mount and again after marking one read; neither
 *   is something the reader is waiting on, so neither should hold the portal.
 */
export function fetchNotifications({background = false} = {}) {
    return axios.get(notifications, {background})
        .then(response => {
            if ([200].includes(Number(response?.status))) {
                return response?.data;
            } else throw new Error(`Unexpected status code: ${response?.status}`)
        });
        // Removed .catch to allow the caller to handle errors.
}

export function patchNotification(notificationId, data) {
    _validateParams({notificationId, data});
    const apiUrl = notifications + notificationId + '/';
    return axios.patch(apiUrl, data)
        .then(response => {
            if ([200, 204].includes(Number(response?.status))) {
                return response?.data;
            } else throw new Error(`Unexpected status code: ${response?.status}`);
        });
        // Removed .catch to allow the caller to handle errors.
}

// ------------------------------------------------
// Contact
// ------------------------------------------------

export function addContact(data) {
    _validateParams({data});
    return _request('post', '/contact/', data, {accepted: [200, 201]});
}

// ------------------------------------------------
// Auth
// ------------------------------------------------

/**
 * Reads the signed-in user from the server.
 *
 * @param {object} [options]
 * @param {boolean} [options.background=false] - Marks a check nobody is waiting for, so that it does not
 *   raise the global loading wash. Handed to axios as config, where the interceptor in index.js reads it.
 */
export function fetchAuthUser({background = false} = {}) {
    return _request('get', '/auth/user/', null, {cache: false, config: {background}});
}

/**
 * Exchanges a customer-portal handoff ticket for a session.
 *
 * The second half of a sign-in that began on a customer's own portal: that portal authenticated them against
 * Microsoft, the API answered it with a ticket, and the portal put the ticket in the address it sent them here
 * with. This spends it. A ticket is single-use and lives about a minute, so a failure here is ordinarily a
 * reload of /EntraHandoff rather than anything wrong — see views/EntraHandoff.
 *
 * @param {string} ticket - the opaque ticket from the handoff fragment.
 * @returns {Promise<{access: string, refresh: string, user: object}>} the session, shaped as a login response.
 */
export function redeemHandoffTicket(ticket) {
    _validateParams({ticket});
    return _request('post', '/auth/token/from-ticket/', {ticket}, {cache: false});
}

// ------------------------------------------------
// Locations
// ------------------------------------------------

export function searchLocationByZip(zip) {
    _validateParams({zip});
    return _request('post', '/locations/', {address: zip});
}

// ------------------------------------------------
// Users
// ------------------------------------------------

/**
 * Method to get users for the current customer
 * @param {Number} page - Page number. Defaults to 1.
 * @param {String} searchQuery - Search query string. Defaults to undefined.
 * @param {String} role - Role filter (e.g., 'admin', 'user'). Defaults to undefined.
 * @param {String} companyFilter - Company filter. The company uuid to filter users by. Defaults to undefined.
 * @param {Boolean} isAdmin - Flag indicating if the user is an admin. Defaults to false.
 * @returns {Promise<axios.AxiosResponse<any> | void>}
 */
export function fetchUsersDetails(page = 1, searchQuery = undefined, role = undefined, companyFilter = undefined, isAdmin = null, customerId = null) {
    const resolvedIsAdmin = isAdmin !== null ? isAdmin
        : !!(getStoredSession()?.user?.features?.includes('admin_dashboard'));
    const resolvedCustomerId = customerId ?? getStoredSession()?.user?.customer_uuid ?? '';

    const params = {};
    if (page) params.page = page;
    if (searchQuery) params.search = searchQuery;
    if (role && role !== "all") params[resolvedIsAdmin ? "role" : "user__role"] = role;
    if (companyFilter && companyFilter !== "all") {
        if (resolvedIsAdmin) {
            if (companyFilter === "tapeark") params["customeruser__isnull"] = true;
            else params["customeruser__customer__uuid"] = companyFilter;
        } else params["customer__uuid"] = companyFilter;
    }

    const path = resolvedIsAdmin ? '/users/' : `/customers/${resolvedCustomerId}/users/`;
    return _request('get', `${path}${_buildQueryString(params)}`, null, {accepted: [200, 201], cache: !searchQuery});
}

const userByEmailCache = new Map(); // email → {user, timestamp}
const USER_BY_EMAIL_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Resolves a Nexus user by their exact email address.
 * Searches through all pages (page size 100) until a match is found or pages exhausted.
 * Results are cached per email with a 5-minute TTL.
 * Admin status is auto-detected.
 * @param {string} email
 * @returns {Promise<object|null>} - Matched user object or null if not found
 */
export async function resolveUserByEmail(email) {
    if (!email) return null;

    const cached = userByEmailCache.get(email);
    if (cached && (Date.now() - cached.timestamp) < USER_BY_EMAIL_TTL) {
        return cached.user;
    }

    const PAGE_SIZE = 100;
    let page = 1;
    let match = null;

    while (true) {
        const data = await fetchUsersDetails(page, email, undefined, undefined, null)
            .catch(() => null);

        const results = data?.results || [];
        match = results.find(u =>
            (u.email || '').toLowerCase() === email.toLowerCase()
        ) || null;

        if (match) break;

        // No more pages
        const total = data?.count || 0;
        if (results.length === 0 || page * PAGE_SIZE >= total) break;

        page++;
    }

    userByEmailCache.set(email, {user: match, timestamp: Date.now()});
    return match;
}

export function addUser(customerId, data, isAdmin = false) {
    if (!isAdmin && !customerId) throw new Error("Customer ID is required to add a user");
    const path = isAdmin ? '/users/' : `/customers/${customerId}/users/`;
    const payload = {
        ...data,
        groups: [data.role],
        is_superuser: data.role === 'admin' ? true : undefined,
        customer: customerId || null,
    };
    return _request('post', path, payload, {accepted: [200, 201]})
        .then(result => { if (result && customerId) invalidateCustomerContactCache(customerId); return result; });
}

export function patchUser(customerId, userId, data, isAdmin = false) {
    if (!isAdmin && !customerId) throw new Error("Customer ID is required to patch a user");
    const path = isAdmin ? `/users/${userId}/` : `/customers/${customerId}/users/${userId}/`;
    return _request('patch', path, data, {accepted: [200, 204]})
        .then(result => { if (result && customerId) invalidateCustomerContactCache(customerId); return result; });
}

export function deleteUser(customerId, userId, isAdmin = false) {
    if (!isAdmin && !customerId) throw new Error("Customer ID is required to delete a user");
    const path = isAdmin ? `/users/${userId}/` : `/customers/${customerId}/users/${userId}/user/`;
    return _request('delete', path, null, {accepted: [200, 204]})
        .then(result => { if (result !== undefined && customerId) invalidateCustomerContactCache(customerId); return result; });
}

// ------------------------------------------------
// User Settings
// ------------------------------------------------

export function fetchAdminUserSettings(userId) {
    _validateParams({userId});
    return _request('get', `/users/${userId}/`);
}

export function fetchCustomerUserSettings(customerId, email = null) {
    _validateParams({customerId});
    return _request('get', `/customers/${customerId}/users/`)
        .then(data => {
            if (email) {
                const user = data?.results?.find(u => u.email === email);
                return user || null;
            }
            return data;
        });
}

export function saveAdminUserSettings(userId, payload) {
    _validateParams({userId, payload});
    return _request('patch', `/users/${userId}/`, payload, {accepted: [200, 204]});
}

export function saveCustomerUserSettings(customerId, userId, payload) {
    _validateParams({customerId, userId, payload});
    return _request('patch', `/customers/${customerId}/users/${userId}/`, payload, {accepted: [200, 204]});
}

export function createOtp(email) {
    _validateParams({email});
    return _request('post', '/auth/generateotp/', {email}, {accepted: [200, 201]});
}

export function createPassword(email, password1, password2, otp) {
    _validateParams({email});
    if (!password1 || !password2) throw new Error("Passwords are required");
    if (!otp) throw new Error("OTP is required");
    return _request('post', '/auth/forgotpassword/', {email, new_password1: password1, new_password2: password2, otp: Number(otp)}, {accepted: [200, 201]});
}